import {
  Box,
  Skeleton as MuiSkeleton,
  Stack,
  styled,
  useMediaQuery,
} from '@mui/material';
import React, { useState } from 'react';
import {
  currentGalleryCarIdSet,
  galleryZoomStepSet,
  getAllGalleryImagesMid,
  getCurrentGalleryCarId,
  getGalleryZoomStep,
} from '../../store/gallery-slice';
import { useDispatch, useSelector } from 'react-redux';
import GalleryButtons from './gallery-buttons';
import theme from '../../config/theme';
import ImageViewer from './image-viewer';
import { getRefValue } from '../../utils/get-ref-value';
import useSwiper from '../../hooks/use-swiper';
import useImageAspectRatio from '../../hooks/use-aspect-ratio';

type GalleryImage = {
  id: string;
  url: string;
};

const Container = styled(Stack)<any>(({ zoomstep }) => ({
  position: 'relative',
  height: '100%',
  width: '100%',
  transition: 'all 0.3s ease-in-out',
  display: zoomstep === '1' ? 'flex' : 'initial',
  justifyContent: 'center',
  alignItems: 'center',
}));

const Image = styled('img')<any>(
  ({ zoomstep, selectedimageindex, index, aspectratio }) => ({
    objectFit: 'cover',
    flexGrow: 0,
    flexShrink: 0,
    maxHeight: '100%',
    cursor: 'grab',
    transform: `scale(${aspectratio})`,
    maxWidth: '100%',
    transition: 'translate3d, transform 300ms ease-in-out',
    borderRadius: theme.spacing(2),
    zIndex:
      zoomstep === '2'
        ? 'initial'
        : selectedimageindex === index
          ? 99
          : 'initial',
  }),
);

const Skeleton = styled(MuiSkeleton)<any>(() => ({
  display: 'block',
  borderRadius: theme.spacing(1),
  boxShadow: '0 0 9px -2px #454545',
  background: '#8d8d8d',
  objectFit: 'cover',
  height: '100%',
  width: '80%',
  flexGrow: 0,
  flexShrink: 0,
}));

const GalleryContainer = styled(Box)<any>(({ offsetx, swipemotion }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  overflow: 'visible',
  touchAction: 'pan-y',
  transform: `translate3d(${offsetx}px, 0, 0)`,
  transition: swipemotion === 'true' ? 'none' : 'transform 0.3s ease-in-out',
}));

const GalleryWrapper = styled(Box)(() => ({
  position: 'relative',
  overflow: 'hidden',
  height: '100%',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  cursor: 'inherit',
  touchAction: 'inherit',
}));

const Wrapper = styled(Box)(() => ({
  display: 'flex',
  width: '100%',
  height: '100% !important',
  alignItems: 'center',
  position: 'relative',
  justifyContent: 'center',
}));

export default function ImagePreview() {
  // Media Queries
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('lg'));
  const isPortrait = useMediaQuery('(orientation: portrait)');

  // Selectors
  const zoomStep = useSelector(getGalleryZoomStep);
  const currentGalleryCarId = useSelector(getCurrentGalleryCarId);
  // Get mid quality gallery images
  const { interiorGalleryMid, exteriorGalleryMid } = useSelector(
    getAllGalleryImagesMid,
  );

  // Custom
  const dispatch = useDispatch();

  // Combine interior and exterior gallery images
  const allGalleryMid = React.useMemo(
    () => [...exteriorGalleryMid, ...interiorGalleryMid],
    [exteriorGalleryMid, interiorGalleryMid],
  );

  const [imagesLoaded, setImagesLoaded] = useState(
    new Array(allGalleryMid.length).fill(false),
  );
  // Find the index of the selected image
  const selectedImageIndex = React.useMemo(() => {
    return allGalleryMid.findIndex(
      (image: GalleryImage) => image.id === currentGalleryCarId,
    );
  }, [allGalleryMid, currentGalleryCarId]);

  // Hooks
  const aspectRatio = useImageAspectRatio(
    allGalleryMid[selectedImageIndex]?.url,
  );

  const handleLoad = (index: number) => {
    setImagesLoaded((prevStatus) => {
      const newStatus = [...prevStatus];
      newStatus[index] = true;
      return newStatus;
    });
  };

  const handleOpenZoomImage = () => {
    dispatch(galleryZoomStepSet(2));
  };

  const handleSelectImageListItem = (index: number) => {
    dispatch(currentGalleryCarIdSet(allGalleryMid[index]?.id));
  };

  const { onTouchStart, offsetX, setOffsetX, swipeMotion, containerRef } =
    useSwiper(handleSelectImageListItem);

  React.useEffect(() => {
    const containerEl = getRefValue(containerRef);
    const containerWidth = containerEl.offsetWidth;
    setOffsetX(-(containerWidth * selectedImageIndex));
  }, [selectedImageIndex, isPortrait]);

  return (
    <Container
      className={isMobile ? '' : 'animate-slide-left'}
      zoomstep={zoomStep.toString()}
    >
      <Wrapper>
        <GalleryButtons />
        <Box sx={{ width: '100%', height: '100%' }}>
          <GalleryWrapper
            onMouseDown={onTouchStart}
            onTouchStart={onTouchStart}
          >
            <GalleryContainer
              offsetx={offsetX}
              ref={containerRef}
              swipemotion={swipeMotion.toString()}
            >
              {allGalleryMid.map((image: GalleryImage, index: number) => (
                <Box
                  key={image?.id}
                  sx={{
                    width: '100%',
                    minWidth: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {!imagesLoaded[index] && (
                    <Skeleton animation="wave" aspectratio={aspectRatio} />
                  )}

                  <Image
                    alt="drivee"
                    aspectratio={aspectRatio}
                    draggable={false}
                    height={imagesLoaded[index] ? 'auto' : '0'}
                    index={index.toString()}
                    loading="lazy"
                    onClick={isMobile ? handleOpenZoomImage : undefined}
                    onDoubleClick={isMobile ? undefined : handleOpenZoomImage}
                    onLoad={() => handleLoad(index)}
                    selectedimageindex={selectedImageIndex.toString()}
                    src={image?.url}
                    width={imagesLoaded[index] ? 'auto' : '0'}
                    zoomstep={zoomStep.toString()}
                  />
                </Box>
              ))}
            </GalleryContainer>
          </GalleryWrapper>
        </Box>
      </Wrapper>
      <ImageViewer image={allGalleryMid[selectedImageIndex]} />
    </Container>
  );
}
